import { Container, Flex, LoadingIcon, Text } from "basis";
import { ContactSection } from "components/Contact/ContactSection/ContactSection";
import { ProductAccordion } from "components/ProductAccordion/ProductAccordion";
import { useAccounts } from "library/accounts";
import { Account } from "library/accounts/types";
import { useProducts } from "library/hooks/useProducts";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CardAccordionContent } from "./CardAccordionContent";
import styles from "./manage-account-content.module.scss";

interface FilteredCards {
  openCC: Account[];
  closedCC: Account[];
}

export const ManageAccountContent = () => {
  const navigate = useNavigate();
  const productsInfo = useProducts();
  const accountsInfo = useAccounts({
    fullCreditCardsInfo: true,
    fullPersonalLoansInfo: true,
  });

  useEffect(() => {
    if (accountsInfo.error?.response?.status === 401) {
      navigate("/login");
    }
  }, [accountsInfo, navigate]);

  if (accountsInfo.isLoading || productsInfo.isLoading || !productsInfo.data) {
    return (
      <Flex height="200" placeItems="center">
        <LoadingIcon size="medium" />
      </Flex>
    );
  }

  const accounts = accountsInfo.data;

  const { openCC, closedCC }: FilteredCards = accounts?.credit_cards
    ?.filter((account) => account?.links.length > 0)
    .reduce(
      (acc: FilteredCards, cur: Account) => {
        const key = cur.status === "CLOSED" ? "closedCC" : "openCC";
        acc[key].push(cur);
        return acc;
      },
      { openCC: [], closedCC: [] },
    ) || {
    openCC: [],
    closedCC: [],
  };
  const userPLProducts =
    accounts?.personal_loans && productsInfo.data
      ? productsInfo.data.filter((product) =>
          accounts.personal_loans.some(
            (account) => account?.product_id === product.id,
          ),
        )
      : [];

  if (!openCC.length && !closedCC.length && !userPLProducts.length) {
    return null;
  }

  const uniqueProducts = userPLProducts.filter(
    (obj, pos, arr) => arr.map((mapObj) => mapObj.id).indexOf(obj.id) === pos,
  );

  if (openCC.length + closedCC.length + uniqueProducts.length === 0) {
    return null;
  }

  return (
    <div className={styles.wrapper}>
      {openCC.map((account) => {
        const product = productsInfo.data?.find(
          (p) => account.product_id === p.id,
        );
        return (
          <div
            className={styles.accordionWrapper}
            data-testid={`credit-card-ending-${account.credit_card?.linked_card_last4}`}
            key={account.account_id}
          >
            <ProductAccordion
              accountIdLabel={
                account.credit_card?.linked_card_last4 &&
                `Card ending ${account.credit_card.linked_card_last4}`
              }
              product={product}
              alwaysOpen={openCC.length + uniqueProducts.length === 1}
            >
              <CardAccordionContent account={account} product={product} />
            </ProductAccordion>
          </div>
        );
      })}
      {uniqueProducts.map((product) => {
        const accountNumber = accounts?.personal_loans.find(
          (account) => account.product_id === product.id,
        )?.account_number;

        return (
          <div className={styles.accordionWrapper} key={product.id}>
            <ProductAccordion
              product={product}
              alwaysOpen={openCC.length + uniqueProducts.length === 1}
              accountIdLabel={
                accountNumber && `Account number ${accountNumber}`
              }
            >
              <div className={styles.plInfo}>
                <Text margin="0 0 6 0">
                  We are working on being able to manage your personal loan
                  online, but for now, please contact us on the details below
                  for anything to do with your loan.
                </Text>
                <ContactSection
                  product={product}
                  className={styles.plContact}
                />
              </div>
            </ProductAccordion>
          </div>
        );
      })}
      {closedCC.length > 0 && (
        <Container width="100%" margin="6 0 0 0">
          <Text textStyle="subtitle2" margin="0 0 2 0">
            <strong>Closed accounts ({closedCC.length})</strong>
          </Text>
          {closedCC.map((account) => {
            const product = productsInfo.data?.find(
              (p) => account.product_id === p.id,
            );
            return (
              <div
                className={styles.accordionWrapper}
                data-testid={`credit-card-ending-${account.credit_card?.linked_card_last4}`}
                key={account.account_id}
              >
                <ProductAccordion
                  accountIdLabel={
                    account.credit_card?.linked_card_last4 &&
                    `Card ending ${account.credit_card.linked_card_last4}`
                  }
                  product={product}
                  alwaysOpen={openCC.length + uniqueProducts.length === 1}
                >
                  <CardAccordionContent account={account} product={product} />
                </ProductAccordion>
              </div>
            );
          })}
        </Container>
      )}
    </div>
  );
};
